import { useEffect, useRef, useState } from "react";
import { Bot, Send, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

function makeSessionId() {
  const k = "ea_chat_session";
  let s = localStorage.getItem(k);
  if (!s) {
    s = "sess-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(k, s);
  }
  return s;
}

const STARTER = {
  role: "assistant",
  content:
    "Hi 👋 I'm Aria, the EuroAsia AI Recruiter. Are you looking to hire talent or looking for a new role?",
};

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([STARTER]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(makeSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", {
        session_id: sessionId.current,
        message: msg,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Sorry — I couldn't reach the AI just now. Please try again, or book a consultation and a human recruiter will reply within 24h.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const quick = (txt) => send(txt);

  return (
    <>
      {!open && (
        <button
          data-testid="chatbot-open"
          aria-label="Open AI Recruiter"
          onClick={() => setOpen(true)}
          className="fixed bottom-24 right-5 z-[60] group inline-flex items-center gap-2 pl-3 pr-4 py-3 rounded-full bg-slate-900 text-white shadow-xl hover:shadow-2xl hover:bg-slate-800 transition-all"
        >
          <div className="w-8 h-8 rounded-full bg-blue-600 grid place-items-center">
            <Bot className="w-4 h-4" />
          </div>
          <span className="font-display font-bold text-sm">AI Recruiter</span>
          <Sparkles className="w-4 h-4 text-blue-300 group-hover:rotate-12 transition-transform" />
        </button>
      )}

      {open && (
        <div
          data-testid="chatbot-panel"
          className="fixed bottom-5 right-5 z-[60] w-[360px] max-w-[calc(100vw-1.5rem)] h-[540px] max-h-[calc(100vh-2.5rem)] rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          <div className="flex items-center justify-between p-3 border-b border-slate-200 bg-slate-900 text-white">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-600 grid place-items-center">
                <Bot className="w-4 h-4" />
              </div>
              <div>
                <div className="font-display font-bold text-sm leading-tight">
                  Aria · AI Recruiter
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-blue-300">
                  Online · GPT-4o-mini
                </div>
              </div>
            </div>
            <button
              data-testid="chatbot-close"
              aria-label="Close chat"
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md hover:bg-white/10"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div
            ref={scrollRef}
            data-testid="chatbot-messages"
            className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-slate-50"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-blue-600 text-white rounded-br-sm"
                      : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm text-slate-500">
                  Aria is typing…
                </div>
              </div>
            )}

            {messages.length <= 1 && (
              <div className="pt-2 flex flex-wrap gap-2">
                {[
                  "I'm hiring",
                  "I'm looking for a job",
                  "Visa for Germany?",
                ].map((q) => (
                  <button
                    key={q}
                    data-testid={`chat-quick-${q.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => quick(q)}
                    className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="p-3 border-t border-slate-200 flex items-center gap-2"
          >
            <Input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message…"
              disabled={loading}
              className="bg-slate-50 border-slate-200"
            />
            <Button
              data-testid="chatbot-send"
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              size="icon"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
}
